import { useCallback, useMemo, useRef, useState } from 'react';
import type { ExpandedState } from '@tanstack/react-table';
import { useQueryStates } from 'nuqs';
import {
  Box,
  DataTable,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderIconTile,
  PageHeaderTitle,
  Paragraph,
  TabNav,
  TabNavList,
  TabNavTab,
} from '@customerio/pluma-components/react';
import { ActivityHistoryChip } from '@/features/billing';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { WorkspaceObjectType } from '@/features/workspaces';
import { useActivityLogEnvironment, useLogs } from '@/services/db/activity-log/queries';
import {
  activityLogFilterParsers,
  createAnonymousColumns,
  createIdentifiedColumns,
  ExpandedRow,
  expandedRowIds,
  LogsFilterControls,
  LogsToolbar,
  newlyExpandedRowIds,
  useActivityTypeAvailability,
  useLogsLoadNew,
  useObjectTypeById,
} from '@/features/activity-feed';
import { activityLogTypeLabel, buildActivityTypeOptions, type ActivityLogTypeTerms } from '@/features/activity-feed';
import { useActivityLogsAnalytics } from './analytics';
import type { IdentifierConfig } from '@/features/activity-feed';
import type { ActivityLogRow, ActivityLogSegment, ActivityLogTab } from '@/features/activity-feed';

interface ActivityLogsPageProps {
  /** Workspace id from the route == API environment_id. */
  workspaceId: string;
  tab: ActivityLogTab;
  /**
   * Href for each tab (the route owns the URL). Rendered as real links via Pluma `TabNav`, which
   * routes these internal hrefs through the app router — so switching tabs is a client-side
   * navigation that preserves the current filters (search params), no click handler needed.
   */
  tabHref: (tab: ActivityLogTab) => string;
}

/**
 * The Anonymous tab hides the filter bar, so the Identified-tab filters (preserved in the URL when
 * you switch tabs) are invisible and unclearable there. Don't send them to the API — the URL keeps
 * them, so switching back to Identified restores them.
 */
const ANONYMOUS_FILTERS = { type: '', name: '', from: '', to: '' };

const TYPE_TERMS: ActivityLogTypeTerms = {
  campaignSingular: 'Automation',
  personSingular: 'Person',
  personPlural: 'People',
};

/** Show the table's loading state on the first load (and while a fresh fetch hasn't returned rows). */
function isInitialTableLoad(isLoading: boolean, isFetching: boolean, rowCount: number): boolean {
  return isLoading || (isFetching && rowCount === 0);
}

function NoLogsState() {
  return (
    <EmptyState>
      <EmptyStateTitle>No activity yet</EmptyStateTitle>
    </EmptyState>
  );
}

function LogsErrorState() {
  return (
    <EmptyState>
      <EmptyStateTitle>Your activity log couldn't load</EmptyStateTitle>
      <EmptyStateDescription>Refresh the page and try again.</EmptyStateDescription>
    </EmptyState>
  );
}

/** Table empty-slot component: the error state on a failed fetch, otherwise the "no data" state. */
function emptyStateForFetch(isError: boolean) {
  return isError ? LogsErrorState : NoLogsState;
}

type ActivityLogTableParams = {
  isAnonymous: boolean;
  /** The route param, which links keep so an alias URL stays on the alias. */
  workspaceId: string;
  /** The resolved numeric id, which anything that talks to the API needs. */
  dataWorkspaceId: string;
  typeTerms: ActivityLogTypeTerms;
  enabledActions: readonly string[];
  currentType: string;
  isBuilder: boolean;
  isGeofenceAvailable: boolean;
  isLiveNotificationsAvailable: boolean;
  isConsentAvailable: boolean;
  isQueryObjectsAvailable: boolean;
  identifiers: IdentifierConfig;
  segmentById: Map<number, ActivityLogSegment>;
  objectTypeById: Map<number, WorkspaceObjectType>;
  refetch: () => void;
  hasMore: boolean;
  fetchMore: () => void;
  isFetchingMore: boolean;
};

/** Builds the DataTable's columns, expanded-row renderer, load-more config, and type options. */
function useActivityLogTable({
  isAnonymous,
  workspaceId,
  dataWorkspaceId,
  typeTerms,
  enabledActions,
  currentType,
  isBuilder,
  isGeofenceAvailable,
  isLiveNotificationsAvailable,
  isConsentAvailable,
  isQueryObjectsAvailable,
  identifiers,
  segmentById,
  objectTypeById,
  refetch,
  hasMore,
  fetchMore,
  isFetchingMore,
}: ActivityLogTableParams) {
  const resolveTypeLabel = useCallback((row: ActivityLogRow) => activityLogTypeLabel(row.log, typeTerms), [typeTerms]);
  const activityTypeOptions = useMemo(
    () =>
      buildActivityTypeOptions(typeTerms, {
        isGeofenceAvailable,
        isLiveNotificationsAvailable,
        isConsentAvailable,
        isQueryObjectsAvailable,
        enabledActions,
        currentType,
      }),
    [
      typeTerms,
      isGeofenceAvailable,
      isLiveNotificationsAvailable,
      isConsentAvailable,
      isQueryObjectsAvailable,
      enabledActions,
      currentType,
    ],
  );

  const expandedRowComponent = useCallback(
    ({ row }: { row: { original: ActivityLogRow } }) => (
      <ExpandedRow
        row={row.original}
        workspaceId={workspaceId}
        dataWorkspaceId={dataWorkspaceId}
        campaignSingular={typeTerms.campaignSingular}
        isAnonymous={isAnonymous}
        identifiers={identifiers}
        segmentById={segmentById}
        onResent={() => refetch()}
        resend={{ source: 'Activity Logs' }}
      />
    ),
    [workspaceId, dataWorkspaceId, typeTerms.campaignSingular, isAnonymous, identifiers, segmentById, refetch],
  );

  const columns = useMemo(
    () =>
      isAnonymous
        ? createAnonymousColumns({ resolveTypeLabel, workspaceId, personSingular: typeTerms.personSingular })
        : createIdentifiedColumns({
            resolveTypeLabel,
            workspaceId,
            personSingular: typeTerms.personSingular,
            objectTypeById,
            isBuilder,
            identifiers,
          }),
    [isAnonymous, resolveTypeLabel, workspaceId, typeTerms.personSingular, objectTypeById, isBuilder, identifiers],
  );

  const loadMore = useMemo(
    () => ({ canLoad: hasMore, onClick: () => fetchMore(), isLoading: isFetchingMore, label: 'Load more' }),
    [hasMore, fetchMore, isFetchingMore],
  );

  return { columns, expandedRowComponent, loadMore, activityTypeOptions };
}

/**
 * Per-tab row expansion, kept above the tab-keyed DataTable so each tab remembers its expanded rows
 * across the remount on tab switch. Newly expanding rows (not collapsing) triggers `onExpand` with
 * their ids — used to report the expansion to analytics.
 */
function usePerTabRowExpansion(tab: ActivityLogTab, onExpand: (newlyExpandedRowIds: string[]) => void) {
  const [expandedByTab, setExpandedByTab] = useState<Record<ActivityLogTab, Record<string, boolean>>>({
    identified: {},
    anonymous: {},
  });
  // The diff runs against a ref, not the rendered state: two expansions dispatched before React
  // commits would both compare to the same stale map, re-reporting the first row as newly expanded
  // and double-firing its event. `onExpand` can't move into the state updater instead — updaters
  // must stay pure, and StrictMode invokes them twice.
  const expandedByTabRef = useRef(expandedByTab);

  const handleRowExpansionChange = useCallback(
    (next: ExpandedState) => {
      const rows = expandedRowIds(next);
      // The remount on tab switch re-seeds from `initiallyExpandedRows`, which diffs to empty —
      // no spurious event for rows the tab already had open.
      const newlyExpanded = newlyExpandedRowIds(expandedByTabRef.current[tab], rows);
      expandedByTabRef.current = { ...expandedByTabRef.current, [tab]: rows };
      setExpandedByTab(expandedByTabRef.current);
      if (newlyExpanded.length > 0) onExpand(newlyExpanded);
    },
    [tab, onExpand],
  );

  return { expandedByTab, handleRowExpansionChange };
}

type TableInteractionsParams = {
  tab: ActivityLogTab;
  rows: ActivityLogRow[];
  tableLoadMore: { canLoad: boolean; onClick: () => void; isLoading: boolean; label: string };
};

/**
 * The two list interactions that carry side effects: expanding a row and loading more. Both are
 * reported to analytics.
 * The page view fires from `useActivityLogsAnalytics` here too, so all of the page's analytics
 * enter through one seam.
 */
function useTableInteractions({ tab, rows, tableLoadMore }: TableInteractionsParams) {
  const { trackMoreLogsLoaded, trackRowsExpanded } = useActivityLogsAnalytics(tab, rows);

  const { expandedByTab, handleRowExpansionChange } = usePerTabRowExpansion(tab, trackRowsExpanded);

  const loadMore = useMemo(
    () => ({
      ...tableLoadMore,
      onClick: () => {
        trackMoreLogsLoaded();
        tableLoadMore.onClick();
      },
    }),
    [tableLoadMore, trackMoreLogsLoaded],
  );

  return { expandedByTab, handleRowExpansionChange, loadMore };
}

/**
 * The workspace id the data layer should use. The route param may be the `last` alias; the
 * data layer needs the resolved numeric id (its cache keys live under `['workspace', <id>]`
 * for workspace-switch eviction). Links keep using the raw param so alias URLs stay on the
 * alias. Returns '' (disabling the queries) until the workspace resolves.
 */
function useDataWorkspaceId(workspaceId: string): string {
  const currentWorkspace = useCurrentWorkspace();
  const numericId = Number(workspaceId);
  return Number.isInteger(numericId) && numericId > 0 ? workspaceId : String(currentWorkspace?.id ?? '');
}

function ActivityLogsPage({ workspaceId, tab, tabHref }: ActivityLogsPageProps) {
  const isAnonymous = tab === 'anonymous';
  const auth = useAuth();
  const dataWorkspaceId = useDataWorkspaceId(workspaceId);
  // Date-range params are read for deep links / the redirect even though the date-range UI is
  // gated behind the `activity-logs-time-range` flag; a value that is not a date reads as absent.
  const [filters, setFilters] = useQueryStates(activityLogFilterParsers, { history: 'replace' });
  const listFilters = isAnonymous ? ANONYMOUS_FILTERS : filters;

  const logs = useLogs({
    environmentId: dataWorkspaceId,
    tab,
    filters: listFilters,
  });
  const { rows, segmentById, isLoading, isError, isFetching, hasMore, fetchMore, isFetchingMore, refetch } = logs;

  const { loadNew, status: newCheckStatus } = useLogsLoadNew({ tab, filters: listFilters, dataWorkspaceId, logs });

  const isBuilder = isBuilderPlan(auth.account);
  const { isGeofenceAvailable, isLiveNotificationsAvailable, isConsentAvailable, isQueryObjectsAvailable } =
    useActivityTypeAvailability();
  const { sentEvents, sentAttributes, identifiers, enabledActions } = useActivityLogEnvironment(dataWorkspaceId);
  const objectTypeById = useObjectTypeById(dataWorkspaceId);

  const {
    columns,
    expandedRowComponent,
    loadMore: tableLoadMore,
    activityTypeOptions,
  } = useActivityLogTable({
    isAnonymous,
    workspaceId,
    dataWorkspaceId,
    typeTerms: TYPE_TERMS,
    enabledActions,
    currentType: filters.type,
    isBuilder,
    isGeofenceAvailable,
    isLiveNotificationsAvailable,
    isConsentAvailable,
    isQueryObjectsAvailable,
    identifiers,
    segmentById,
    objectTypeById,
    refetch,
    hasMore,
    fetchMore,
    isFetchingMore,
  });

  // Row expansion (held per tab so it survives the tab-keyed DataTable remount) and load-more,
  // both of which report to analytics.
  const { expandedByTab, handleRowExpansionChange, loadMore } = useTableInteractions({
    tab,
    rows,
    tableLoadMore,
  });

  return (
    <Page flexGrow="1" data-test-activity-logs-page>
      <PageHeader>
        <PageHeaderIconTile name="activity" />
        <PageHeaderTitle>Profile activity</PageHeaderTitle>
        <PageHeaderActions>
          <ActivityHistoryChip kind="profile" />
        </PageHeaderActions>
      </PageHeader>
      <PageBody>
        <TabNav aria-label="Profile activity">
          <TabNavList>
            <TabNavTab href={tabHref('identified')} isActive={tab === 'identified'}>
              Identified
            </TabNavTab>
            <TabNavTab href={tabHref('anonymous')} isActive={tab === 'anonymous'}>
              Anonymous
            </TabNavTab>
          </TabNavList>
        </TabNav>

        <Box mt="200" mb="100">
          <LogsToolbar newCheckStatus={newCheckStatus}>
            {isAnonymous ? (
              <Paragraph>Events for last 24 hours.</Paragraph>
            ) : (
              <LogsFilterControls
                environmentId={dataWorkspaceId}
                filters={filters}
                setFilters={(values) => void setFilters(values)}
                activityTypeOptions={activityTypeOptions}
                sentEvents={sentEvents}
                sentAttributes={sentAttributes}
              />
            )}
          </LogsToolbar>
        </Box>

        <DataTable
          // Key by tab: the two tabs have different column sets, and DataTable holds column
          // order in internal (uncontrolled) state. Sharing one instance across tabs lets the
          // Identified order bleed onto Anonymous — `anonymous_id` (absent from that order) gets
          // appended last. A per-tab instance renders each column set in its definition order.
          key={tab}
          caption="Activity log entries"
          data={rows}
          columns={columns}
          getRowId={(row) => row.log.id}
          isLoading={isInitialTableLoad(isLoading, isFetching, rows.length)}
          // A failed fetch shows the error state in the same (empty) slot as "no data". The table
          // only renders it when there are no rows, so a mid-pagination error keeps the loaded rows.
          emptyStateComponent={emptyStateForFetch(isError)}
          // Pluma draws the "load new" row only when its empty state is off. With new entries to
          // load, turn the empty state off so the row shows; the list then loads them at once.
          withEmptyState={loadNew.canLoad !== true}
          layout="fixed"
          withSorting={false}
          withRowExpanding
          getRowCanExpand={() => true}
          expandedRowComponent={expandedRowComponent}
          initiallyExpandedRows={expandedByTab[tab]}
          onRowExpansionChange={handleRowExpansionChange}
          loadNew={loadNew}
          loadMore={loadMore}
        />
      </PageBody>
    </Page>
  );
}

export default ActivityLogsPage;
